import { monthNames } from "../constants/utils";
import moment from "moment";
import useSubscriptionPermission from "../hooks/useSubscriptionPermission";
import useUserPermission from "../hooks/useUserPermission";
import { apiUrl } from "../constants/API";
import { decryptData, encryptData } from "./crypto";
import axios from "axios";
import { useSelector } from "react-redux";
import { store } from "../store";

const multipliers = { k: 1000, m: 1000000, b: 1000000000 };

const validUrl =
  /^(https?:\/\/)(www\.)[a-z0-9]+(\.[a-z]{2,}){1,3}(#?\/?[a-zA-Z0-9#]+)*\/?(\?[a-zA-Z0-9-_]+=[a-zA-Z0-9-%]+&?)?$/;

export const InternationalFormat = (num, decimalValue = 0) => {
  if (!num) return num;
  return Intl.NumberFormat("en-US", {
    notation: "compact",
    maximumFractionDigits:
      num > 10000 && num < 1000000 ? decimalValue + 0 : decimalValue + 1,
  }).format(num);
};

export const msToSec = (ms) => {
  return Math.floor(ms / 1000); // or Math.round depending on your use case
};

export const humanNumConverter = (string) => {
  if (/[a-zA-Z]/.test(string.charAt(0))) {
    return "";
  }
  let letter = string.charAt(string.length - 1).toLowerCase();
  if (
    !["k", "m", "b"].includes(letter) &&
    !isNaN(Number(string.slice(0, string.length - 1)))
  ) {
    return string.slice(0, string.length - 1);
  } else if (isNaN(Number(string.slice(0, string.length - 1))))
    letter = string.charAt(string.length - 2).toLowerCase();
  return parseFloat(string) * multipliers[letter];
};

export const getLastMonth = () => {
  const currentDate = new Date();
  const lastMonthIndex = currentDate.getMonth() - 1;
  const currentYear = currentDate.getFullYear();
  const lastMonthName = monthNames[lastMonthIndex];
  return { lastMonthName, currentYear };
};

export const calculateWeeksBetweenDates = (start, end) => {
  if (!start || !end) {
    return "N/A";
  }

  const startDate = new Date(start);
  const endDate = new Date(end);

  if (isNaN(startDate) || isNaN(endDate)) {
    return "N/A";
  }

  const millisecondsPerWeek = 7 * 24 * 60 * 60 * 1000;
  const differenceInMilliseconds = endDate.getTime() - startDate.getTime();

  return Math.ceil(differenceInMilliseconds / millisecondsPerWeek);
};

export const isFieldRequired = (validationSchema, id) =>
  validationSchema
    .describe()
    .fields[id].tests.some(({ name }) => name === "required");

export const getAuthToken = () => {
  const token = store.getState().auth.token
  return token ? `Bearer ${token}` : null 
}
export const clearReduxState = () => localStorage.removeItem("persist:root");
export const removeEmptyFields = (data) => {
  Object.keys(data).forEach((key) => {
    if (data[key] === "" || data[key] == null) {
      delete data[key];
    } else if (
      Array.isArray(data[key]) &&
      data[key].length === 1 &&
      data[key][0] === ""
    ) {
      delete data[key];
    }
  });
};

export const removeUndefinedFields = (data) => {
  for (const [key, value] of Object.entries(data)) {
    if (value === undefined) {
      delete data[key];
    }
  }
};

export const getOptions = (data) => {
  return data
    ?.filter((category) => category !== "")
    ?.map((category) => {
      return { value: category, label: category?.trim() };
    });
};

export const platformPriority = (platforms) => {
  const platformArray = Object.keys(platforms);
  if (platformArray.includes("instagram")) return "instagram";
  else if (platformArray.includes("youtube")) return "youtube";
  else if (platformArray.includes("facebook")) return "facebook";
};

export const isValidDate = (value) => {
  // Try creating a new Date object from the value
  const date = new Date(value);

  // Check if the date is valid and not NaN
  return !isNaN(date) && date instanceof Date;
};

export const isValidLink = (value) => {
  let validUrlPattern = validUrl;
  if (value === "") {
    validUrlPattern = /^$/;
  }
  return value === null || value?.match(validUrlPattern);
};
// check it is selected
export const isSelected = (selectedCreators, _id, platform) =>
  selectedCreators?.some((i) => i?._id === _id && i?.platform === platform);

// check it is selected for animeta certified users page
export const isUserSelected = (selectedusers, creatorId, platfrom) =>
  selectedusers?.some((i) => i?.creatorId === creatorId && i?.platform === platfrom);
// generate platform based data
export const generatePlatformData = (data, platformKey = "platform") => ({
  instagramCreatorsId: data
    ?.filter((i) => i?.[platformKey] === "instagram")
    ?.map((i) => i?._id || i?.creatorId),
  facebookCreatorsId: data
    ?.filter((f) => f?.[platformKey] === "facebook")
    ?.map((f) => f?._id || f?.creatorId),
  youtubeCreatorsId: data
    ?.filter((y) => y?.[platformKey] === "youtube")
    ?.map((y) => y?._id || y?.creatorId),
  tiktokCreatorsId: data
    ?.filter((y) => y?.[platformKey] === "tiktok")
    ?.map((y) => y?._id || y?.creatorId),
  snapchatCreatorsId: data
    ?.filter((y) => y?.[platformKey] === "snapchat")
    ?.map((y) => y?._id || y?.creatorId),
});

// Common for Video Embeded
export const getEmbedId = (videoUrl) => {
  let matches = [];
  if (videoUrl && videoUrl !== "NA") {
    if (videoUrl.includes("instagram.com")) {
      matches = videoUrl.match(/instagram.com\/p\/([A-Za-z0-9_.-]+)/);
    } else if (videoUrl.includes("facebook.com")) {
      matches = videoUrl.match(/facebook.com\/video.php\?v=([A-Za-z0-9_.-]+)/);
    } else if (videoUrl.includes("youtube.com")) {
      matches =
        videoUrl.match(/youtube.com\/watch\?v=([A-Za-z0-9_.-]+)/) ||
        videoUrl.match(/youtube.com\/shorts\/([A-Za-z0-9_.-]+)/);
    } else matches[1] = " ";
  }
  return matches && matches[1];
};

//Only for performance
export const getPerformanceEmbedId = (videoUrl) => {
  let matches = [];
  if (videoUrl && videoUrl !== "NA") {
    //Instagram validation
    if (videoUrl.includes("instagram.com")) {
      if (videoUrl?.includes("stories")) {
        matches = videoUrl.match(
          /instagram.com\/stories\/([A-Za-z0-9_.-]+)\/([0-9]+)/
        );
      } else if (videoUrl?.includes("reel")) {
        matches = videoUrl.match(/instagram.com\/reel\/([A-Za-z0-9_.-]+)/);
      } else {
        matches = videoUrl.match(/instagram.com\/p\/([A-Za-z0-9_.-]+)/);
      }
    }

    //facebook validations
    else if (videoUrl.includes("facebook.com") || videoUrl.includes("fb")) {
      if (videoUrl.includes("reel")) {
        matches = videoUrl.match(/facebook.com\/reel\/([A-Za-z0-9_.-]+)/);
      } else if (videoUrl?.includes("photo")) {
        matches = videoUrl.match(
          /^https:\/\/www\.facebook\.com\/photo\/\?fbid=[0-9]+(&set=[a-zA-Z0-9._%-]+(&opaqueCursor=[a-zA-Z0-9._%-]+)?)?$/
        );
      } else if (videoUrl.includes("watch")) {
        matches =
          videoUrl.match(/facebook.com\/watch\/\?v=[0-9]+/) ||
          videoUrl.match(/fb.watch\/[a-zA-Z0-9_-]+/);
      } else {
        if (videoUrl.match(/facebook\.com\/[a-zA-Z0-9.]+\/videos\/[0-9]+/)) {
          matches = videoUrl.match(
            /facebook\.com\/[a-zA-Z0-9.]+\/videos\/[0-9]+/
          );
        } else {
          matches = videoUrl.match(
            /facebook\.com\/[a-zA-Z0-9.]+\/videos\/[^\/]+\/[0-9]+/
          );
        }
      }
    }
    //youtube validations
    else if (videoUrl.includes("youtube.com")) {
      matches =
        videoUrl.match(/youtube.com\/watch\?v=([A-Za-z0-9_.-]+)/) ||
        videoUrl.match(/youtube.com\/shorts\/([A-Za-z0-9_.-]+)/);
    } 
    else if (videoUrl.includes("snapchat.com/spotlight/")) {
      matches = videoUrl.match(/snapchat.com\/spotlight\/([A-Za-z0-9_-]+)/);
    }    
    else matches[1] = " ";

  }
  return matches && matches;
};

export const TableDivision = ({
  keyValue,
  defaultValue,
  className = "",
  valueWrapper = "",
  mainLabel = "",
  isSublabel = false,
  subLabelClass = "",
  subLabel,
  key,
  ...props
}) => {
  return (
    <td {...props} className={className} key={key}>
      {!isSublabel ? (
        <div className={valueWrapper}>{keyValue ? keyValue : defaultValue}</div>
      ) : (
        <div className={valueWrapper}>
          <div className={mainLabel}>{keyValue ? keyValue : defaultValue}</div>
          <div className={subLabelClass}>{subLabel}</div>
        </div>
      )}
    </td>
  );
};

export const getDatesRange = (startDate, endDate, interval) => {
  const date = new Date(startDate?.getTime());

  const dates = [];

  while (date <= endDate) {
    dates.push(new Date(date));
    date.setDate(date.getDate() + 1);
  }

  return dates;
};
export const isSafariBrowser = () => {
  const userAgent = window.navigator.userAgent;

  const isChrome =
    /Chrome/.test(userAgent) && /Google Inc/.test(navigator.vendor);

  const isSafari = /Safari/.test(userAgent) && !isChrome;

  return isSafari;
};

export const truncate = (source, size) => {
  return source?.length > size ? source?.slice(0, size - 1) + "…" : source;
};

export const generateURLSlug = (text) =>
  text?.replace(/\s+/g, "-").toLowerCase();

export const capitalizeFirstLetter = (str) =>
  str?.charAt(0).toUpperCase() + str?.slice(1).toLowerCase();

export const generateOptions = (options) =>
  options?.map((option) => ({
    label: capitalizeFirstLetter(option),
    value: option,
  }));

export const removeEmptyNestedFields = (obj) => {
  if (
    obj === null ||
    obj === undefined ||
    obj === "" ||
    (Array.isArray(obj) && obj.length === 0)
  ) {
    return undefined;
  }

  if (Array.isArray(obj)) {
    const filteredArray = obj
      .map((item) => removeEmptyNestedFields(item))
      .filter((item) => item !== undefined);

    return filteredArray.length > 0 ? filteredArray : undefined;
  } else if (typeof obj === "object") {
    const newObj = {};
    for (const key in obj) {
      const fieldValue = removeEmptyNestedFields(obj[key]);
      if (fieldValue !== undefined) {
        newObj[key] = fieldValue;
      }
    }
    return Object.keys(newObj).length > 0 ? newObj : undefined;
  } else {
    return obj;
  }
};

export const creatorProfileDataConverter = (data) => {
  // converter function
  const stringConvertToOption = (option) =>
    option?.value ? option?.value : undefined,
    arrayConvertToOption = (options) =>
      options?.length
        ? options?.map((option) => stringConvertToOption(option))
        : undefined,
    toStringBool = (bool) =>
      ["true", "false"].includes(bool) ? bool === "true" : undefined;
  // personalDetails conversions
  data.creator_type = stringConvertToOption(data.creator_type);
  data.state = stringConvertToOption(data.state);
  data.location = stringConvertToOption(data.location);
  data.country = stringConvertToOption(data.country);

  // agency details conversion
  if (data?.agencyDetails?.length) {
    data.agencyDetails = data.agencyDetails.map((agency) => ({
      ...agency,
      _id: undefined,
      currentUse: toStringBool(agency?.currentUse),
    }));
  }
  // content details conversion
  const contentData = data?.contentDetails;
  data.contentDetails = {
    // contentLanguage: arrayConvertToOption(contentData?.contentLanguage),
    primaryLanguage: contentData?.primaryLanguage?.value,
    secondaryLanguage: contentData?.secondaryLanguage?.value,
    content: arrayConvertToOption(contentData?.content),
    brandCategories: arrayConvertToOption(contentData?.brandCategories),
    categories: arrayConvertToOption(contentData?.categories),
    barterCampaign: toStringBool(contentData?.barterCampaign),
    affiliateCampaign: toStringBool(contentData?.affiliateCampaign),
    snapCategories: contentData.snapCategories?.value,
    snapSubCategories: contentData.snapSubCategories?.value
  };
  data.content_genre = arrayConvertToOption(data?.content_genre);
  // pricing details conversions
  for (const platform in data?.pricingDetails) {
    const platformValue = data?.pricingDetails?.[platform];
    data.pricingDetails[platform] = {
      ...platformValue,
      packageDeliverable: arrayConvertToOption(
        platformValue?.packageDeliverable
      ),
      deliverables: platformValue?.deliverables?.map((deliverable) => ({
        ...deliverable,
        _id: undefined,
        deliverable: stringConvertToOption(deliverable?.deliverable),
        negotiableRange: stringConvertToOption(deliverable?.negotiableRange),
      })),
    };
  }
  // document conversion
  data.documents = data.documents?.map((doc) => ({
    ...doc,
    idCardDocument: stringConvertToOption(doc.idCardDocument),
    idCardDocumentURL:
      typeof doc?.idCardDocumentURL === "string"
        ? doc?.idCardDocumentURL
        : undefined,
    idCardDocumentURLName: undefined,
    _id: undefined,
  }));
  // addition contact details conversions
  data.contactDetails = data.contactDetails?.map((contact) => ({
    ...contact,
    _id: undefined,
  }));
  data.publishedStatusOfAnimetaVerified = undefined;
  data = removeEmptyNestedFields(data);
  return data;
};

export const dateConverter = (date, format) =>
  moment(date, "YYYY-MM-DDTHH:mm:ss.SSSZ").format(format);

export const covertNumToLocalStr = (number) => number.toLocaleString("en-IN");

export const accessNestedFields = (obj, name) => {
  const keys = name
    .split(".")
    ?.map((key) => (isNaN(Number(key)) ? key : Number(key)));
  switch (keys.length) {
    case 1:
      return obj?.[keys?.[0]];
    case 2:
      return obj?.[keys?.[0]]?.[keys?.[1]];
    case 3:
      return obj?.[keys?.[0]]?.[keys?.[1]]?.[keys?.[2]];
  }
};

export const handleValuesConverter = (data, type) => {
  switch (type) {
    case "rupee":
      return data
        ? `₹ ${covertNumToLocalStr(Math.round(Number(data)))}`
        : undefined;
    case "regularNumber":
      return data ? InternationalFormat(data) : undefined;
    case "percentage":
      return ["0", null, undefined].includes(data)
        ? undefined
        : `${InternationalFormat(data)}%`;
    case "withPercentage":
      return ["0%", null, undefined].includes(data) ? undefined : data;
    default:
      return data;
  }
};

// common clear function for location filters
export const clearLocationFilter = (locationData) =>
  locationData?.map((country) => ({
    ...country,
    isActive: false,
    isPartial: false,
    level1: country?.level1?.map((state) => ({
      ...state,
      isActive: false,
      isPartial: false,
      search: "",
      level2: state?.level2?.map((city) => ({
        ...city,
        isActive: false,
      })),
    })),
  }));

  
// Clear function for Category filter in snapchat
export const clearCategoryFilter = (categoryData) =>
  categoryData?.map((topLevel) => ({
    ...topLevel,
    isActive: false,
    isPartial: false,
    level0: topLevel?.level0?.map((category) => ({
      ...category,
      isActive: false,
      isPartial: false,
      search: "",
      level1: category?.level1?.map((subcategory) => ({
        ...subcategory,
        isActive: false,
      })),
    })),
   
  }));
  
 
  
export const checkLocationSelected = (locationData) =>
  locationData?.some((data) => data.isActive || data?.isPartial);
export const checkAudienceSelected = (audienceData) =>
  audienceData?.some((data) => data?.male || data?.female || data?.value);

export const checkAudienceFilter = (gender, minAge, maxAge, percent, GenderPercentage) =>
  gender || minAge > 13 || maxAge < 64 || percent > 0 || GenderPercentage > 0;

export const checkSnapAudienceFilter = (gender, minAge, maxAge, percent, GenderPercentage) =>
  gender || minAge > 13 || maxAge < 35 || percent > 0 || GenderPercentage > 0;

// handle scroll for popup for top and bottom
export const handleScroll = (type, id) => {
  const popupContent = document.getElementById(id);
  if (popupContent) {
    popupContent.scrollTop = type === "top" ? 0 : popupContent.scrollHeight;
  }
};

export const truncateDecimalPlaces = (number, decimalPlaces) => {
  const factor = Math.pow(10, decimalPlaces);
  return Math.floor(number * factor) / factor;
};

export const isIndia = (currentCountry) => currentCountry === "India";

export const convertLocationArrayPayload = (locationData) => {
  let data = {
    level0: [],
    level1: [],
    level2: [],
  };
  data.level0 = data?.level0?.concat(
    locationData
      ?.filter((country) => {
        if (country.isActive) return true;
        else if (country.isPartial)
          data.level1 = data?.level1?.concat(
            country?.level1
              ?.filter((state) => {
                if (state.isActive) return true;
                else if (state.isPartial)
                  data.level2 = data?.level2?.concat(
                    state?.level2
                      ?.filter((city) => {
                        if (city.isActive) return true;
                        return false;
                      })
                      ?.map((city) => city.name)
                  );
                return false;
              })
              ?.map((state) => state.name)
          );
        return false;
      })
      ?.map((country) => country.name)
  );
  return data;
};

// Convert Category and subcategory arrya from paylod function for  snapchat filter
export const convertCategoryArrayPayload = (categoryData) => {
  let data = {
      level0: [],
      level1: [],
      level2: [],
  };

  data.level0 = data?.level0?.concat(
      categoryData?.filter((all) => {
          if (all.isActive) return true;
          else if (all.isPartial)
              data.level1 = data?.level1?.concat(
                  all?.level0
                      ?.filter((category) => {
                          if (category.isActive) return true;
                          else if (category.isPartial)
                              data.level2 = data?.level2?.concat(
                                  category?.level1
                                      ?.filter((subcategory) => subcategory.isActive)
                                      ?.map((subcategory) => {
                                        if (!data.level1.includes(category.name)) {
                                          data.level1.push(category.name);
                                        }
                                        return subcategory.name
                                      })
                              );
                          return false;
                      })
                      ?.map((category) => category.name)
              );
          return false;
      })
      ?.map((all) => all.name)
  );

  return data;
};

//Path based key align
export const getSubscriptionPath = (path) => {
  switch (path) {
    case "dashboard":
      return "dashboard";
    case "creators":
      return "creators";
    case "campaigns":
      return "campaign";
    case "collabs":
      return "globalCollabs";
    case "rising-star":
      return "rising_stars";
    case "my-list":
      return "my_list";
    case "brand":
      return "brands";
    case "creatorProfile":
      return "creatorProfile";
    case "campaignSetup":
      return "campaignSetup";
    case "creatorMixRecommendation":
      return "creatorMixRecommendation";
    case "campaignMonitoring":
      return "campaignMonitoring";
    case "campaignManagement":
      return "campaignManagement";
    case "campaignExport":
      return "campaignExport";
    case "creatorExport":
      return "creatorExport";
    case "affiliateMarketing":
      return "affiliateMarketing";
    case "shortlistCreators":
      return "shortlistCreators";
    case "shortlistCreatorsExport":
      return "shortlistCreatorsExport";
    case "brandAffiliate":
      return "brandAffiliate";
    default:
      return null;
  }
};

//Path based key align
export const getUserAccess = (path) => {
  switch (path) {
    case "campaigns":
      return "campaign";
    case "collabs":
      return "globalCollabs";
    case "rising-star":
      return "rising_stars";
    case "my-list":
      return "my_list";
    case "brand":
      return "brands";
    case "users":
      return "users";
    case "organization":
      return "organisation";
    case "creatorProfile":
      return "creatorProfile";
    default:
      return null;
  }
};

//Subcription Access Function

export const SubscriptionAccess = (module) => {
  const permission = useSubscriptionPermission(module);
  return permission;
};

export const UserAccess = (module) => {
  const permission = useUserPermission(module);
  return permission;
};

//Dynamically Rendering API service function
// export const GetBaseURL = (type, baseUrl) => {
//   const version = type === "v1" ? "v1" : "v2"; // Determining the version based on the 'type' parameter
//   //Providing a default service value if dynamicUrl is not set in localStorage
//   return `${apiUrl}/${baseUrl ||
//     localStorage.getItem('dynamicUrl')}/api/${version}`;
// };
export const GetBaseURL = (type, baseUrl) => {
  const version = type === "v1" ? "v1" : "v2"; // Determining the version based on the 'type' parameter

  //get this url based on Animeta core
  const apiUrl = baseUrl === "animeta-core"
    ? "https://brandstar-backend-apps.animeta.ai/animeta-core"
    : localStorage.getItem("dynamicUrl");

  // Construct the full URL
  return `${apiUrl}/api/${version}`;
};

export const generateFilename = (fileName, fileExtension) => {
  const now = new Date();
  const padToTwoDigits = (num) => num.toString().padStart(2, "0");

  const date = padToTwoDigits(now.getDate());
  const month = padToTwoDigits(now.getMonth() + 1); // Months are zero-indexed
  const year = now.getFullYear();

  return `${date}-${month}-${year} ${fileName}.${fileExtension}`;
};

export const handleConverMillisecToSec = (data) => {
  if(data){
    let seconds = Math.round(data/1000)
    return `${seconds}(sec)`;
  }
  else return "--";
}

// Create Axios instance
export const axiosInstance = axios.create();

// Request Interceptor for Encryption
axiosInstance.interceptors.request.use(
  (config) => {
    if (config.data) {
      config.data = encryptData(config.data);
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response Interceptor for Decryption
axiosInstance.interceptors.response.use(
  (response) => {
    if (response?.data?.data) {
      response.data.data = decryptData(response.data.data);
    }
    return response;
  },
  (error) => {
    return Promise.reject(error);
  }
);

export const validImageUrl = (url) => {
  try {
    new URL(url);
    return true;
  } catch (_) {
    return false;
  }
};


// for add config dropdown
export const getFilteredPlatforms = (currentIndex, watchBuyNowLinks, platformList) => {
  if (!Array.isArray(platformList)) return [];

  const selectedPlatforms = watchBuyNowLinks
    ?.map((item, i) => (i !== currentIndex ? item?.platform?.value : null))
    .filter(Boolean); // remove nulls

  const currentValue = watchBuyNowLinks?.[currentIndex]?.platform?.value;

  return platformList.filter((platform) => {
    return !selectedPlatforms.includes(platform.value) || platform.value === currentValue;
  });
};
